import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { PartialStar, starState } from "@/components/ReaderReviews";
import { adminErrorDetail } from "../lib/adminError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "deleted", label: "Deleted" },
];

export default function AdminReviews() {
  const [authed, setAuthed] = useState(null);
  const [csrf, setCsrf] = useState("");
  const [pw, setPw] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("pending");
  const [actionError, setActionError] = useState("");
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);

  const fetchReviews = useCallback(async () => {
    const res = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
    setReviews(res.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/admin/me`, { withCredentials: true });
        setCsrf(me.data.csrf_token);
        setAuthed(true);
        await fetchReviews();
      } catch {
        setAuthed(false);
      }
    })();
  }, [fetchReviews]);

  const login = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw }, { withCredentials: true });
      setCsrf(res.data.csrf_token);
      setAuthed(true);
      setPw("");
      await fetchReviews();
    } catch (err) {
      setLoginError(
        typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong."
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setAuthed(false);
    setCsrf("");
    setReviews([]);
  };

  const call = async (method, path, body) => {
    setActionError("");
    try {
      const res = await axios.request({
        method,
        url: `${API}${path}`,
        data: body,
        headers: { "X-CSRF-Token": csrf },
        withCredentials: true,
      });
      return res.data;
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
        return null;
      }
      setActionError(
        adminErrorDetail(err, "Action failed.")
      );
      return null;
    }
  };

  const doAction = async (method, path) => {
    const res = await call(method, path);
    if (res) await fetchReviews();
  };

  const setVerified = async (r, value) => {
    setVerifyingId(r.id);
    const res = await call("patch", `/reviews/${r.id}/verified`, { verified_download: value });
    if (res) {
      setReviews((prev) =>
        prev.map((x) => (x.id === r.id ? { ...x, verified_download: res.verified_download } : x))
      );
    }
    setVerifyingId(null);
  };

  const confirmPurge = async () => {
    const target = purgeTarget;
    setPurgeTarget(null);
    const res = await call("delete", `/reviews/${target.id}/purge`);
    if (res) await fetchReviews();
  };

  const grouped = {
    pending: reviews.filter((r) => !r.deleted_at && r.status === "pending"),
    approved: reviews.filter((r) => !r.deleted_at && r.status === "approved"),
    rejected: reviews.filter((r) => !r.deleted_at && r.status === "rejected"),
    deleted: reviews.filter((r) => r.deleted_at),
  };

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

  const btn = "label-mono border border-hairline px-3 py-1.5 hover:bg-ink hover:text-white transition-colors";
  const btnDanger = "label-mono border border-red-200 text-red-600 px-3 py-1.5 hover:bg-red-600 hover:text-white transition-colors";

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper" data-testid="admin-reviews-loading">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6" data-testid="admin-reviews-login">
        <form onSubmit={login} className="w-full max-w-sm">
          <div className="label-mono text-muted">Reviews admin</div>
          <h1 className="font-display text-4xl md:text-5xl mt-6 mb-10 leading-tight">Enter password.</h1>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Password"
            className="w-full bg-transparent border-b border-ink py-3 text-lg outline-none"
            data-testid="admin-reviews-password-input"
            autoFocus
          />
          {loginError && (
            <p className="mt-4 text-sm text-red-600" data-testid="admin-reviews-login-error">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            disabled={loginLoading}
            className="mt-8 w-full bg-ink text-white py-4 label-mono hover:bg-leaf transition-colors disabled:opacity-60"
            data-testid="admin-reviews-login-submit"
          >
            {loginLoading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  const rows = grouped[tab];

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-reviews-dashboard">
      <header className="border-b border-hairline sticky top-0 z-10 backdrop-blur-md bg-white/85">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6 overflow-x-auto">
            <span className="font-display text-lg whitespace-nowrap" style={{ fontWeight: 800 }}>
              Reviews
            </span>
            <div className="flex items-center gap-1 border border-hairline p-1">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`label-mono px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${
                    tab === t.key ? "bg-ink text-white" : "text-ink hover:bg-surface"
                  }`}
                >
                  {t.label} ({grouped[t.key].length})
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={logout}
            className="label-mono text-muted hover:text-ink transition-colors ml-4"
            data-testid="admin-reviews-logout"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <h2 className="font-display text-3xl md:text-4xl mb-2 capitalize">{tab} reviews</h2>
        {tab === "deleted" && (
          <p className="text-muted text-sm mb-6">
            Soft-deleted reviews. Restore returns each review to its stored status. Permanently delete cannot be undone.
          </p>
        )}
        {actionError && (
          <p className="mb-6 text-sm text-red-600" data-testid="admin-reviews-action-error">
            {actionError}
          </p>
        )}

        {rows.length === 0 ? (
          <p className="text-muted mt-6" data-testid="admin-reviews-empty">
            No {tab} reviews.
          </p>
        ) : (
          <div className="border-t border-hairline divide-y divide-hairline mt-6">
            {rows.map((r) => (
              <div key={r.id} className="py-6 grid grid-cols-12 gap-6 items-start" data-testid={`review-row-${r.id}`}>
                <div className="col-span-12 md:col-span-3">
                  <div className="label-mono text-muted">{fmt(r.created_at)}</div>
                  <div className="mt-2 flex gap-0.5" aria-label={`${r.rating} out of 5 stars`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <PartialStar key={n} state={starState(r.rating, n)} size={18} />
                    ))}
                  </div>
                  <div className={`mt-1 font-display text-lg leading-tight ${r.name ? "" : "text-muted italic"}`}>
                    {r.name || "Anonymous trader"}
                  </div>
                  <a href={`mailto:${r.email}`} className="text-sm text-ink hover:text-leaf transition-colors break-all">
                    {r.email}
                  </a>
                  <div className="mt-2 label-mono text-muted" data-testid={`subscriber-indicator-${r.id}`}>
                    {r.subscriber_before_review ? "Existing ebook subscriber" : "Added through review form"}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <p className="text-ink whitespace-pre-wrap leading-relaxed">{r.text}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    {tab !== "deleted" && (
                      <label className="flex items-center gap-2 cursor-pointer select-none mr-2">
                        <input
                          type="checkbox"
                          checked={!!r.verified_download}
                          disabled={verifyingId === r.id}
                          onChange={(e) => setVerified(r, e.target.checked)}
                          data-testid={`verified-checkbox-${r.id}`}
                          className="w-4 h-4 accent-black disabled:opacity-50"
                        />
                        <span className={`text-sm ${verifyingId === r.id ? "opacity-50" : ""}`}>Verified download</span>
                      </label>
                    )}
                    {tab === "pending" && (
                      <>
                        <button onClick={() => doAction("patch", `/reviews/${r.id}/approve`)} className={btn} data-testid={`btn-approve-${r.id}`}>
                          Approve
                        </button>
                        <button onClick={() => doAction("patch", `/reviews/${r.id}/reject`)} className={btn} data-testid={`btn-reject-${r.id}`}>
                          Reject
                        </button>
                        <button onClick={() => doAction("delete", `/reviews/${r.id}`)} className={btnDanger} data-testid={`btn-delete-${r.id}`}>
                          Delete
                        </button>
                      </>
                    )}
                    {tab === "approved" && (
                      <button onClick={() => doAction("delete", `/reviews/${r.id}`)} className={btnDanger} data-testid={`btn-delete-${r.id}`}>
                        Delete
                      </button>
                    )}
                    {tab === "rejected" && (
                      <>
                        <button onClick={() => doAction("patch", `/reviews/${r.id}/pending`)} className={btn} data-testid={`btn-pending-${r.id}`}>
                          Return to pending
                        </button>
                        <button onClick={() => doAction("patch", `/reviews/${r.id}/approve`)} className={btn} data-testid={`btn-approve-${r.id}`}>
                          Approve
                        </button>
                        <button onClick={() => doAction("delete", `/reviews/${r.id}`)} className={btnDanger} data-testid={`btn-delete-${r.id}`}>
                          Delete
                        </button>
                      </>
                    )}
                    {tab === "deleted" && (
                      <>
                        <span className="label-mono text-muted mr-2">
                          Status: {r.status} · Deleted {fmt(r.deleted_at)}
                        </span>
                        <button onClick={() => doAction("patch", `/reviews/${r.id}/restore`)} className={btn} data-testid={`btn-restore-${r.id}`}>
                          Restore
                        </button>
                        <button onClick={() => setPurgeTarget(r)} className={btnDanger} data-testid={`btn-purge-${r.id}`}>
                          Permanently delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {purgeTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          data-testid="purge-confirm-dialog"
        >
          <div className="bg-white max-w-md w-full p-8 border border-hairline">
            <p className="text-lg font-semibold text-ink">
              Permanently delete this review? This cannot be undone.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setPurgeTarget(null)}
                className="label-mono border border-hairline px-4 py-2 hover:bg-surface transition-colors"
                data-testid="purge-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={confirmPurge}
                className="label-mono bg-red-600 text-white px-4 py-2 hover:bg-red-700 transition-colors"
                data-testid="purge-confirm-button"
              >
                Permanently delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
